import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import ReportPageLayout from "@/components/reports/layout/ReportPageLayout";
import ReportFilterPanel, {
    ReportDateInput,
    ReportNumberInput,
    ReportSelectInput,
} from "@/components/reports/layout/ReportFilterPanel";
import ReportDataGrid from "@/components/reports/layout/ReportDataGrid";
import FclReportView from "@/components/reports/FclReportView";
import { getReportDefinition } from "@/components/reports/fclReportDefinitions";
import { buildReportColumns } from "@/pages/reports/reportTableHelper";
import { REPORT_EXPORT_EXCLUDE_KEYS } from "@/pages/reports/shared/reportExportData";
import { useFclReportLoader, useMountReportLoad } from "@/pages/reports/shared/useFclReportLoader";

const FclStandardReportPage = ({ config }) => {
    const reportList = useSelector(config.selectList);
    const reportSummary = useSelector(config.selectSummary);
    const reportSections = useSelector(config.selectSections || (() => ({})));

    const [filters, setFilters] = useState(config.defaultFilters);
    const { loadReport } = useFclReportLoader(config.fetchAction, config.buildParams);

    useMountReportLoad(loadReport, filters);

    const definition = config.reportKey ? getReportDefinition(config.reportKey) : null;

    const resetFilters = () => {
        const next = config.defaultFilters();
        setFilters(next);
        loadReport(next);
    };

    const exportSheets = useMemo(() => {
        const listColumns =
            definition?.listColumns ||
            buildReportColumns(reportList || [], { excludeKeys: REPORT_EXPORT_EXCLUDE_KEYS });

        const sheets = [
            {
                name: definition?.listTitle || config.gridTitle || "Report",
                data: reportList || [],
                columns: listColumns,
            },
        ];
        if (definition?.sections) {
            definition.sections.forEach((section) => {
                const data = reportSections?.[section.id];
                if (data?.length) {
                    sheets.push({
                        name: section.title,
                        data,
                        columns: section.columns,
                    });
                }
            });
        }
        if (reportSummary && Object.keys(reportSummary).length > 0) {
            sheets.push({ name: "Summary", data: [reportSummary] });
        }
        return sheets;
    }, [config.gridTitle, definition, reportList, reportSections, reportSummary]);

    const serverPagination = useMemo(() => {
        if (!config.showServerPaginationFields) return null;

        const summaryTotal =
            reportSummary?.totalRecords ??
            reportSummary?.TotalRecords ??
            reportSummary?.totalRecord ??
            reportSummary?.TotalRecord;
        const parsedSummaryTotal = Number(summaryTotal);
        const totalRecords =
            summaryTotal != null &&
            summaryTotal !== "" &&
            !Number.isNaN(parsedSummaryTotal) &&
            parsedSummaryTotal > 0
                ? parsedSummaryTotal
                : reportList?.length ?? 0;

        return {
            page: filters.IPageNo || 1,
            pageSize: filters.IPageRecords || 25,
            totalRecords,
            onPageChange: (page) => {
                const next = { ...filters, IPageNo: page };
                setFilters(next);
                loadReport(next);
            },
            onPageSizeChange: (size) => {
                const next = { ...filters, IPageRecords: size, IPageNo: 1 };
                setFilters(next);
                loadReport(next);
            },
        };
    }, [config.showServerPaginationFields, filters, reportSummary, reportList, loadReport]);

    const filterColumns = config.showServerPaginationFields
        ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-5"
        : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4";

    const content = config.reportKey ? (
        <FclReportView
            reportKey={config.reportKey}
            filters={filters}
            summary={reportSummary}
            primaryList={reportList}
            sections={reportSections}
            showReportByFilter={config.showReportBy !== false}
            showKpiSummary={config.showKpiSummary !== false}
            serverPagination={serverPagination}
        />
    ) : (
        <ReportDataGrid
            gridId={config.id}
            data={reportList}
            title={config.gridTitle}
            defaultPageSize={config.showServerPaginationFields ? filters.IPageRecords || 25 : 10}
            paginationTitle="records"
            serverPagination={serverPagination}
            columnOptions={{ excludeKeys: REPORT_EXPORT_EXCLUDE_KEYS }}
        />
    );

    return (
        <ReportPageLayout
            compactTop={config.compactTop}
            title={config.title}
            breadcrumb={config.breadcrumb}
            hubPath={config.hubPath}
            onRefresh={() => loadReport(filters)}
            exportSheets={exportSheets}
            exportFileName={config.title}
            filterPanel={
                <ReportFilterPanel
                    onSubmit={() => loadReport(filters)}
                    onReset={resetFilters}
                    columns={filterColumns}
                >
                    <ReportDateInput
                        label="From date"
                        name="FromDate"
                        value={filters.FromDate}
                        onChange={(field, val) => setFilters((p) => ({ ...p, [field]: val ?? "" }))}
                    />
                    <ReportDateInput
                        label="To date"
                        name="ToDate"
                        value={filters.ToDate}
                        onChange={(field, val) => setFilters((p) => ({ ...p, [field]: val ?? "" }))}
                    />
                    {config.showReportBy !== false && (
                        <ReportSelectInput
                            label="Report by"
                            value={filters.ReportBy}
                            onChange={(e) => setFilters((p) => ({ ...p, ReportBy: e.target.value }))}
                            options={config.reportByOptions}
                        />
                    )}
                    {config.showSalesPersonId && (
                        <ReportNumberInput
                            label="Sales person ID"
                            value={filters.SalesPersonId ?? ""}
                            onChange={(e) =>
                                setFilters((p) => ({ ...p, SalesPersonId: e.target.value }))
                            }
                            placeholder="Optional"
                        />
                    )}
                    {config.showServerPaginationFields && (
                        <>
                            <ReportNumberInput
                                label="Page no."
                                value={filters.IPageNo}
                                onChange={(e) =>
                                    setFilters((p) => ({
                                        ...p,
                                        IPageNo: Number(e.target.value) || 1,
                                    }))
                                }
                                min={1}
                            />
                            <ReportNumberInput
                                label="Records per page (API)"
                                value={filters.IPageRecords}
                                onChange={(e) =>
                                    setFilters((p) => ({
                                        ...p,
                                        IPageRecords: Number(e.target.value) || 25,
                                    }))
                                }
                                min={1}
                            />
                        </>
                    )}
                </ReportFilterPanel>
            }
        >
            {content}
        </ReportPageLayout>
    );
};

export default FclStandardReportPage;
